import React, { useState, useEffect, useContext } from 'react';
import {
  Typography, Table, Button, Input, Switch, Space, message, Modal, Form, InputNumber, Card, Tag,
} from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import api from '../api';
import { UserContext } from '../App';

export default function ComplianceConfigPage() {
  const currentUser = useContext(UserContext);
  const isAdmin     = currentUser?.Role === 'admin' || currentUser?.Role === 'qa_admin';
  const [issues, setIssues]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editIssue, setEditIssue] = useState(null);
  const [saving, setSaving]   = useState(false);
  const [form] = Form.useForm();

  // Days an employee has to acknowledge a completed record. One setting for the
  // whole app: compliance has no per-form window the way evaluations and errors
  // do. Changing it moves the deadline for records completed afterwards only --
  // a record is stamped with its deadline when it completes.
  const [ackDays,      setAckDays]      = useState(null);
  const [ackIsDefault, setAckIsDefault] = useState(true);
  const [ackDefault,   setAckDefault]   = useState(10);
  const [ackDraft,     setAckDraft]     = useState(null);
  const [ackSaving,    setAckSaving]    = useState(false);

  const loadAckDays = async () => {
    try {
      const res = await api.get('/api/compliance/config/ack-days');
      setAckDays(res.data.days);
      setAckDraft(res.data.days);
      setAckIsDefault(res.data.is_default);
      setAckDefault(res.data.default);
    } catch { /* leaves the section showing nothing rather than a wrong number */ }
  };

  useEffect(() => { loadAckDays(); }, []);

  const saveAckDays = async () => {
    setAckSaving(true);
    try {
      const res = await api.put('/api/compliance/config/ack-days', { days: ackDraft });
      setAckDays(res.data.days);
      setAckIsDefault(false);
      message.success('Acknowledge deadline saved.');
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to save.');
    } finally {
      setAckSaving(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/compliance/issues');
      setIssues(res.data);
    } catch {
      message.error('Failed to load compliance issues.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditIssue(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true });
    setModalOpen(true);
  };

  const openEdit = (issue) => {
    setEditIssue(issue);
    form.setFieldsValue({ name: issue.Name, is_active: !!issue.IsActive, estimated_effort_hrs: issue.EstimatedEffortHrs ?? '' });
    setModalOpen(true);
  };

  const handleSave = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      if (editIssue) {
        await api.put(`/api/compliance/issues/${editIssue.ID}`, {
          name: values.name,
          is_active: values.is_active,
          estimated_effort_hrs: values.estimated_effort_hrs ?? null,
        });
        message.success('Issue updated.');
      } else {
        await api.post('/api/compliance/issues', {
          name: values.name,
          estimated_effort_hrs: values.estimated_effort_hrs ?? null,
        });
        message.success('Issue created.');
      }
      setModalOpen(false);
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (issue) => {
    try {
      await api.put(`/api/compliance/issues/${issue.ID}`, { is_active: !issue.IsActive });
      setIssues((prev) =>
        prev.map((iss) => iss.ID === issue.ID ? { ...iss, IsActive: issue.IsActive ? 0 : 1 } : iss)
      );
    } catch {
      message.error('Failed to update.');
    }
  };

  const columns = [
    { title: 'ID',   dataIndex: 'ID',   key: 'ID',   width: 60 },
    { title: 'Name', dataIndex: 'Name', key: 'Name', ellipsis: true },
    {
      title: 'Est. Effort (hrs)', dataIndex: 'EstimatedEffortHrs', key: 'EstimatedEffortHrs', width: 150,
      render: (v) => v != null ? `${v} hr${v === 1 ? '' : 's'}` : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Active', dataIndex: 'IsActive', key: 'IsActive', width: 90,
      render: (v, record) => (
        <Switch
          checked={!!v}
          size="small"
          disabled={!isAdmin}
          onChange={isAdmin ? () => toggleActive(record) : undefined}
        />
      ),
    },
    ...(isAdmin ? [{
      title: '', key: 'actions', width: 70, align: 'right',
      render: (_, record) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>Edit</Button>
      ),
    }] : []),
  ];

  return (
    <>
      <Card size="small" style={{ marginBottom: 20 }}
        title={
          <Space>
            <span>Acknowledge Deadline</span>
            {ackIsDefault && ackDays != null && <Tag>using default</Tag>}
          </Space>
        }
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Days an employee has to acknowledge a compliance record once it is completed. The deadline is stamped
          on each record when it completes, so changing this moves the deadline for records completed afterwards
          and leaves ones already issued alone. Records left unacknowledged are acknowledged by the system a
          while after the deadline. Default is {ackDefault} days.
        </Typography.Paragraph>
        <Space>
          <InputNumber
            min={1}
            max={365}
            value={ackDraft}
            onChange={setAckDraft}
            disabled={!isAdmin || ackDays == null}
            style={{ width: 110 }}
            addonAfter="days"
          />
          <Button
            type="primary"
            loading={ackSaving}
            disabled={!isAdmin || ackDraft == null || ackDraft === ackDays}
            onClick={saveAckDays}
          >
            Save
          </Button>
          {!isAdmin && <Typography.Text type="secondary">Admin access required to change this.</Typography.Text>}
        </Space>
      </Card>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Compliance Issue Types</Typography.Title>
        {isAdmin && <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add Issue</Button>}
      </div>

      <Table
        rowKey="ID"
        dataSource={issues}
        columns={columns}
        loading={loading}
        size="small"
        pagination={false}
        locale={{ emptyText: 'No compliance issues configured.' }}
      />

      <Modal maskClosable={false}
        title={editIssue ? 'Edit Issue' : 'New Issue'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        okText="Save"
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="e.g. Attendance Policy Violation" />
          </Form.Item>
          <Form.Item label="Estimated Effort (hrs)" name="estimated_effort_hrs"
            help="Enter hours as a decimal, e.g. 0.25 = 15 min, 0.5 = 30 min, 1 = 1 hr">
            <InputNumber min={0} step={0.25} precision={2} placeholder="e.g. 0.5" style={{ width: '100%' }} />
          </Form.Item>
          {editIssue && (
            <Form.Item label="Active" name="is_active" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </>
  );
}
